import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import TypeaheadSearchResult, { TypeaheadSearchResultItem } from './typeaheadSearchResult';

export interface TypeaheadSearchProps {
  id: string;
  resultList: TypeaheadSearchResultItem[];
  defaultValue?: TypeaheadSearchResultItem | TypeaheadSearchResultItem[] | null;
  placeholder?: string;
  type?: string;
  valueSelected?: Function;
  multiValueSelected?: Function;
  minWidth?: number;
  maxWidth?: number;
  multiSelect?: boolean;
  min?: number;
  max?: number;
}

export interface TypeaheadSettings {
  id: string | null;
  resultWidth?: number;
  resultList?: TypeaheadSearchResultItem[] | [];
  visible?: boolean;
  multiSelect?: boolean;
  selectedResult?: TypeaheadSearchResultItem;
  multiSelectedResult?: TypeaheadSearchResultItem[];
  activeResult?: boolean;
}

const TypeaheadSearch = ({
  id,
  resultList,
  defaultValue,
  valueSelected,
  placeholder,
  type,
  minWidth,
  maxWidth,
  multiSelect,
  min,
  max,
}: TypeaheadSearchProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const typeAheadRef = useRef<HTMLDivElement>(null);
  const multiSelectRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLButtonElement>(null);

  const [input, setInput] = useState({ value: '' });
  const [inputFocus, setInputFocus] = useState(false);
  const [filteredResultList, setFilteredResultList] = useState<TypeaheadSearchResultItem[]>([]);
  const [resultVisible, setResultVisible] = useState(false);
  const [multiSelectedItemsVisible, setMultiSelectedItemsVisible] = useState(false);
  const [selectedResult, setSelectedResult] = useState<TypeaheadSearchResultItem | null>(null);
  const [multiSelectedResult, setMultiSelectedResult] = useState<TypeaheadSearchResultItem[] | null>(null);
  const [resultWidth, setResultWidth] = useState(100);
  const [mouseOver, setMouseOver] = useState(false);

  // -----------------------------------------------------------------------------------
  //  useEffect - resultList - sorts the result list
  //  ----------------------------------------------------------------------------------

  useEffect(() => {
    if (resultList) {
      setFilteredResultList(sortList(resultList));
    }
  }, [resultList]);

  // -----------------------------------------------------------------------------------
  //  useEffect - defaultValue - triggers when the default value is added/changed
  //  ----------------------------------------------------------------------------------

  useEffect(() => {
    const isArray = defaultValue && (defaultValue as any)?.label === undefined;
    if (defaultValue && id) {
      setInput({ value: '' });
      setSelectedResult(null);
      setMultiSelectedResult(null);
      setResultVisible(false);

      if (isArray) {
        if (valueSelected) {
          const defaultItem: TypeaheadSearchResultItem[] = defaultValue as TypeaheadSearchResultItem[];
          setMultiSelectedResult([...defaultItem]);
          //valueSelected(defaultItem);
        }
      } else {
        const defaultItem: TypeaheadSearchResultItem = defaultValue as TypeaheadSearchResultItem;
        setInput({ value: defaultItem.label });
        if (valueSelected) {
          valueSelected(defaultItem);
        }
      }
    }
  }, [defaultValue, id]);

  useEffect(() => {
    if (!resultVisible) {
      window.removeEventListener('click', clickOutside);
    }
  }, [resultVisible]);

  useEffect(() => {
    if (multiSelectedResult && valueSelected) {
      valueSelected(_.cloneDeep(multiSelectedResult));
    }
  }, [multiSelectedResult]);

  // --------------------------------------------------------------------------------------------------
  //  sortList - Sorts the result list in alphabetical order
  //  -------------------------------------------------------------------------------------------------

  const sortList = (list: TypeaheadSearchResultItem[]) => {
    return list.sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
  };

  // --------------------------------------------------------------------------------------------------
  //  onFocus - When the search input field gets focus
  //  -------------------------------------------------------------------------------------------------

  const onFocus = () => {
    showResult();
  };

  // --------------------------------------------------------------------------------------------------
  //  onBlur - When the search input field loses focus
  //  -------------------------------------------------------------------------------------------------

  const onBlur = (e: React.FormEvent<HTMLInputElement>) => {
    if (!multiSelect) {
      e.preventDefault();
      e.stopPropagation();

      //if (!activeTypeaheadSearch.activeResult) {
      //setResultVisible(false);
      if (resultList.length) {
        if ((defaultValue as TypeaheadSearchResultItem).label) {
          setInput({ value: (defaultValue as TypeaheadSearchResultItem).label || '' });
        } else {
          setInput({ value: (defaultValue as TypeaheadSearchResultItem[])[1].label || '' });
        }
      }

      setInputFocus(false);
      //  }
    }
  };

  // --------------------------------------------------------------------------------------------------
  //  onChange - When the search input field text is changed
  //  -------------------------------------------------------------------------------------------------

  const onChange = (e: React.FormEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;

    setInput({ value: inputValue });

    if (filteredResultList.length) {
      if (inputValue.length > 0) {
        setResultVisible(true);
        setFilteredResultList(resultList.filter((item) => item.label.toLowerCase().includes(inputValue.toLowerCase())));
      } else {
        setResultVisible(true);
        setFilteredResultList(sortList(resultList));
      }
    } else {
      //input field
      if (valueSelected) {
        valueSelected({ value: inputValue, label: inputValue });
      }
    }
  };

  // --------------------------------------------------------------------------------------------------
  //  onKeyDown - press up/down to navigate the search result and select by pressing enter
  //  -------------------------------------------------------------------------------------------------

  const onKeyDown = (e: any) => {
    const resultList = filteredResultList;
    const listIndex = resultList.findIndex((listItem) => listItem.id === selectedResult?.id) || 0;

    if (resultList && resultList.length > 0) {
      if (e.keyCode === 38 && listIndex > 0) {
        setSelectedResult(resultList[listIndex - 1]);
      } else if (e.keyCode === 40 && listIndex < resultList.length - 1) {
        setSelectedResult(resultList[listIndex + 1]);
      }

      if (e.keyCode === 13) {
        setResultVisible(false);
      }
    }
  };

  const showResult = () => {
    if (typeAheadRef.current && ref.current) {
      const element = ref.current;
      const typeAheadElement = typeAheadRef.current;
      const inputElement = inputRef.current; //element.querySelector('.typeahead-search-input') as HTMLInputElement;

      if (id) {
        setResultVisible(true);
        setFilteredResultList(sortList(resultList));
        setSelectedResult(null);
        setMultiSelectedResult([...(multiSelectedResult || [])]);

        if (inputElement?.clientWidth) {
          setResultWidth(inputElement?.clientWidth);
        }
        setInputFocus(true);

        window.addEventListener('click', clickOutside);
      }
    }
  };

  // --------------------------------------------------------------------------------------------------
  //  clickOutside - if the user click out side of the dropdown menu
  //  -------------------------------------------------------------------------------------------------

  const clickOutside = (e: MouseEvent) => {
    const clickedElement = e.target as Node;
    const element = ref.current as Node;

    if (element && !element.contains(clickedElement)) {
      window.removeEventListener('click', clickOutside);
      setResultVisible(false);
    }
  };

  // --------------------------------------------------------------------------------------------------
  //  openDropDown - opens the dropdown
  //  -------------------------------------------------------------------------------------------------

  const openDropDown = () => {
    const inputElement = inputRef.current;
    if (inputElement) {
      if (resultVisible) {
        setResultVisible(false);
        inputElement.blur();
      } else {
        showResult();
      }
    }
  };

  // --------------------------------------------------------------------------------------------------
  //  multiSelectChange - when a multiselect result item is checked/unchecked
  //  -------------------------------------------------------------------------------------------------

  const multiSelectChange = (item: TypeaheadSearchResultItem, checked: boolean) => {
    if (multiSelectedResult) {
      if (checked) {
        const itemExists = multiSelectedResult.some((resultItem) => resultItem?.id === item.id);
        if (!itemExists) {
          multiSelectedResult.push(item);
          setMultiSelectedResult([...multiSelectedResult]);
        }
      } else {
        const resultItemToRemove = multiSelectedResult.find((resultItem) => resultItem?.id === item.id);
        if (resultItemToRemove) {
          uncheckResultItem(resultItemToRemove);
        }
      }
    }
  };

  // --------------------------------------------------------------------------------------------------
  //  uncheckResultItem - when a multiselect result item is unchecked from top
  //  -------------------------------------------------------------------------------------------------

  const uncheckResultItem = (item: TypeaheadSearchResultItem) => {
    if (item && multiSelectedResult) {
      setTimeout(() => {
        const i = multiSelectedResult.findIndex((resultItem) => resultItem.id === item.id);
        multiSelectedResult.splice(i, 1);

        setMultiSelectedResult([...multiSelectedResult]);
      }, 200);
    }
  };

  // --------------------------------------------------------------------------------------------------
  //  onResultItemClick - when a result item is clicked
  //  -------------------------------------------------------------------------------------------------

  const onResultItemClick = (item: TypeaheadSearchResultItem) => {
    if (resultList && valueSelected) {
      setResultVisible(false);
      setSelectedResult(item);
      setInput({ value: item.label });
      valueSelected(item);
    }
  };

  const multiSelectionString: string | null = useMemo(() => {
    if (multiSelectedResult) {
      if (multiSelectedResult?.length === 1) {
        return `${multiSelectedResult[0]?.label}`;
      } else if (multiSelectedResult?.length > 1) {
        return `${multiSelectedResult[0]?.label} +${multiSelectedResult?.length - 1}`;
      }
    }
    return null;
  }, [multiSelectedResult]);

  // --------------------------------------------------------------------------------------------------
  //  JSX
  //  -------------------------------------------------------------------------------------------------

  return (
    <div
      ref={ref}
      className={`typeahead-search ${!resultList.length ? 'no-dropdown' : ''}`}
      style={{ minWidth: `${minWidth}px`, maxWidth: `${maxWidth}px` }}
    >
      {multiSelect ? (
        <div
          ref={multiSelectRef}
          className={`selected-multiselect-items ${resultVisible && multiSelectedResult?.length ? 'show' : ''}`}
        >
          <ul>
            {multiSelectedResult?.map((selectedResult, index) => (
              <li key={selectedResult?.id} className="selected-multiselect-item">
                <div className="item-text">{selectedResult?.label}</div>
                <button
                  ref={selectedItemRef}
                  type="button"
                  className="selected-multiselect-items-close-button  "
                  onClick={() => {
                    uncheckResultItem(selectedResult);
                  }}
                >
                  <div className="selected-multiselect-items-close-icon bi bi-x"></div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div ref={typeAheadRef} className="typeahead-search-wrapper">
        <input
          ref={inputRef}
          className="typeahead-search-input"
          type={type || 'text'}
          min={min}
          max={max}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={resultVisible ? placeholder : multiSelectionString || placeholder}
          style={{ minWidth: `${minWidth}px`, maxWidth: `${maxWidth}px` }}
          value={input.value}
        />
        {resultList.length ? (
          <button type="button" className="typeahead-search-input-button" onClick={openDropDown}>
            <span className="dropdown-icon bi bi-caret-down-fill"></span>
          </button>
        ) : null}
      </div>
      <TypeaheadSearchResult
        id={id}
        resultWidth={inputRef.current?.clientWidth}
        resultList={filteredResultList}
        visible={resultVisible}
        multiSelect={multiSelect}
        selectedResult={selectedResult}
        multiSelectedResult={multiSelectedResult}
        onItemClick={onResultItemClick}
        multiSelectChange={multiSelectChange}
      />
    </div>
  );
};

export default TypeaheadSearch;
